import Handlebars from "handlebars"
import { normalize } from "./runtime"
import { collectVars, isGlobal, itemWrites } from "./vars"

// The rules of the validator section of the design notes, numbered the same. Pure and isomorphic: the editor
// shows what it finds and the authoring agent will read it to correct itself. Errors block a save, warnings
// are something the author should have chosen. Out for now: 8 (a silent cycle can only go through a reset, so
// 10.d covers it), 20, 20.c and 21 (judgment, too noisy to check) and 23, which needs the live conversations

const OPERATORS = ["==", "!=", ">", ">=", "<", "<="]
const TESTS = ["isSet", "isEmpty"]
const HELPERS = ["if","unless", "each", "with", "lookup"]
const AI_ONLY = ["prompt", "context", "transitions", "references", "captures", "tools"] as const

interface Place {
  flow: string
  index: number
  item: PlaybookItem
}

type Writer = PlaybookOption | PlaybookTransition

// The texts of an object that have a translation, and the name the issues give it
interface Text {
  what: string
  target?: object & { translations?: PlaybookTranslations<object> }
  fields: string[]
}

export function validate(playbook: Playbook): PlaybookIssue[] {
  const issues: PlaybookIssue[] = []
  const flows: { [flow: string]: PlaybookItem[] } = { main: [], ...Object.fromEntries(Object.entries(playbook.flows || {}).map(([flow, f]) => [flow, f.items || []])) }
  const places = Object.entries(flows).flatMap(([flow, items]) => items.map((item, index): Place => ({ flow, index, item })))
  const written = new Set(collectVars(playbook))
  const reads = new Map<string, Place | undefined>()

  const error = (rule: string, message: string, place?: Place) => issues.push(issue("error", rule, message, place))
  const warning = (rule: string, message: string, place?: Place) => issues.push(issue("warning", rule, message, place))
  const find = (cursor?: PlaybookCursor) => cursor ? places.find(p => p.flow === cursor.flow && p.item.id === cursor.item) : undefined
  const read = (name: string, place?: Place) => { if (!reads.has(name)) reads.set(name, place) }

  // ── The playbook ──

  if (!flows.main.length) error("10.d", "main has no items: the conversation has nowhere to start")
  else if (flows.main.every(i => i.conditions?.length)) error("10.d", "every item of main has conditions: a reset could walk past all of them and say nothing")
  if (playbook.config?.on_lost && !find(playbook.config.on_lost)) error("6.h", `on_lost points at ${label(playbook.config.on_lost)}, which does not exist`)
  if (places.some(p => p.item.type === "ai") && !playbook.config?.model) error("14.f", "there are ai items but the playbook has no model")
  if (places.some(p => p.item.classify) && !playbook.config?.classify_model && !playbook.config?.model) error("14.f", "there are items that understand free text but the playbook has no model")
  template(playbook.defaults?.on_no_match?.say, "the default no-match text")

  // ── Languages: the codes, then every translatable text in each supported language, item by item below ──

  const language = playbook.config?.language || "en"
  const languages = playbook.config?.languages || []
  languageCode(language, "the default language")
  languages.forEach((code, i) => {
    languageCode(code, "a supported language")
    if (code === language) error("lang", `${code} is the default language and also a supported one`)
    else if (languages.indexOf(code) < i) error("lang", `${code} is a supported language twice`)
  })
  translations([{ what: "the default no-match text", target: playbook.defaults?.on_no_match, fields: ["say"] }])

  // ── Flows ──

  for (const [flow, items] of Object.entries(flows)) {
    if (!items.length) { if (flow !== "main") warning("10.c", `the flow ${flow} has no items`); continue }

    const ids = new Set<string>()
    items.forEach((item, index) => {
      const place = { flow, index, item }
      if (!item.id.trim()) error("2", "the item has no id", place)
      else if (ids.has(item.id)) error("2", `the id ${item.id} is already used in this flow`, place)
      ids.add(item.id)
    })

    // 10.b: a run of filtered items has to end on one without conditions, or it falls off the end
    const last = items.length - 1
    const start = items.findIndex((item, index) => item.conditions?.length && items.slice(index).every(i => i.conditions?.length))
    if (start >= 0) error("10.b", "this item and every one below it have conditions: when none holds the conversation falls off the end of the flow", { flow, index: start, item: items[start] })

    const end = { flow, index: last, item: items[last] }
    if (end.item.type === "ai") { if (!end.item.transitions?.length) warning("10.e", "last item of the flow with no transitions: exit falls off the end and resets", end) }
    else if (!end.item.options?.some(o => o.action && o.action.type !== "next")) error("10.c", "the last item of a flow needs an option that goes somewhere: anything else falls off the end and resets", end)
  }

  // ── Items ──

  for (const place of places) {
    const { item } = place
    const ai = item.type === "ai"

    if (!item.say && !item.image && !ai) error("7", "the item says nothing, so there is no question to answer", place)
    if (!ai && !item.options?.length && !item.set) error("10", "the item has no options and captures nothing: it waits for a message it cannot use", place)
    item.conditions?.forEach(c => condition(c, place))

    if (ai) {
      if (!item.prompt?.trim()) error("14.b", "an ai item needs a prompt", place)
      if (item.set) error("14.c", "an ai item does not capture the raw message: declare a capture instead", place)
      if (item.options?.length) error("14.d", "an ai item has no options: its ways out are transitions", place)
      if (!item.transitions?.length && !/\bexit\b/i.test(`${item.say} ${item.prompt}`)) error("14.e", "no transitions and nothing mentions exit: the contact has no way out", place)
      item.transitions?.forEach((t, i) => {
        if (!t.description?.trim()) error("14.e", `transition ${i + 1} has no description, so the model cannot know when to call it`, place)
        outcome(t.action, place, `transition ${i + 1}`)
      })
      tools(item, place)
    }
    else {
      const declared = AI_ONLY.filter(key => Array.isArray(item[key]) ? (item[key] as any[]).length : item[key])
      if (declared.length) error("14.b", `only ai items use ${declared.join(", ")}`, place)
      if (item.classify && !item.options?.some(o => o.label)) error("classify", "understanding free text needs options with labels to pick from", place)
      options(item, place)
    }
    if (ai && item.classify) error("classify", "only a deterministic item can understand free text: an ai item already does", place)

    template(item.say, "the say", place)
    template(item.context, "the context", place)
    template(item.on_no_match?.say, "the no-match text", place)
    item.options?.forEach(o => template(o.say, `the say of ${o.label || "an option"}`, place))
    for (const writer of [...item.options || [], ...item.transitions || []]) reenter(writer, place)
    translations([
      { what: "the item", target: item, fields: ["say"] },
      ...(item.options || []).map((o, i) => ({ what: o.label || `option ${i + 1}`, target: o, fields: ["label", "match", "say"] })),
      { what: "the no-match text", target: item.on_no_match, fields: ["say"] },
    ], place)
  }

  // ── Reachability: from main[0] and on_lost, along every way the cursor can move ──

  const next = (place: Place) => flows[place.flow][place.index + 1] ? at(place.flow, place.index + 1) : at("main", 0)
  const target = (action: PlaybookOutcome | undefined, place: Place) =>
    !action || action.type === "next" ? next(place) : action.type === "reset" ? at("main", 0) : find(action)

  function successors(place: Place): Place[] {
    const { item } = place
    const out: (Place | undefined)[] = []
    if (item.conditions?.length || (!item.say && !item.image && item.type !== "ai")) out.push(next(place))
    if (item.type === "ai") out.push(...(item.transitions?.length ? item.transitions.map(t => target(t.action, place)) : [next(place)]))
    else if (item.options?.length) out.push(...item.options.map(o => target(o.action, place)))
    else out.push(next(place))
    return out.filter(Boolean) as Place[]
  }

  function reach(from: Place[]): Set<Place> {
    const seen = new Set<Place>()
    const stack = [...from]
    while (stack.length) {
      const place = stack.pop()!
      if (seen.has(place)) continue
      seen.add(place)
      stack.push(...successors(place))
    }
    return seen
  }

  const reached = reach([at("main", 0), find(playbook.config?.on_lost)].filter(Boolean) as Place[])
  for (const place of places) if (flows.main.length && !reached.has(place)) warning("6", "nothing leads to this item", place)

  // 20.b: a gate on a var the item writes only fires if the conversation can come back to it
  for (const place of places) {
    const own = new Set(itemWrites(place.item))
    const gated = (place.item.conditions || []).map(c => c[0]).filter(name => own.has(name))
    if (gated.length && !reach(successors(place)).has(place)) error("20.b", `gated on ${gated.join(", ")}, which it writes, but nothing comes back to it: the condition can never matter`, place)
  }

  // 15, loosely: what is read somewhere and written nowhere. A warning, since the platform may seed it
  for (const [name, place] of reads) if (!written.has(name) && !isGlobal(name)) warning("15", `${name} is read but nothing in the playbook writes it`, place)

  return issues.sort((a, b) => (a.level === b.level ? 0 : a.level === "error" ? -1 : 1))

  // ── Rules by part ──

  function at(flow: string, index: number): Place | undefined {
    return places.find(p => p.flow === flow && p.index === index)
  }

  function outcome(action: PlaybookOutcome | undefined, place: Place, owner: string) {
    if (action?.type === "goto" && !find(action)) error("1", `${owner} goes to ${label(action)}, which does not exist`, place)
  }

  function condition(c: PlaybookCondition, place: Place) {
    if (!c?.[0]) return error("14", "a condition names no var", place)
    read(c[0], place)
    if (c.length === 2 && !TESTS.includes(c[1] as string)) error("14", `${c[1]} is not a test: use isSet or isEmpty`, place)
    if (c.length === 3 && !OPERATORS.includes(c[1] as string)) error("14", `${c[1]} is not an operator: use ${OPERATORS.join(" ")}`, place)
    if (c.length > 3) error("14", `the condition on ${c[0]} has too many parts`, place)
  }

  function options(item: PlaybookItem, place: Place) {
    const list = item.options || []
    if (list.length && list.every(o => o.conditions?.length)) error("6.g", "every option has conditions: when none holds the item has nothing to offer", place)

    const seen = new Set<string>()
    list.forEach((option, i) => {
      const name = option.label || `option ${i + 1}`
      if (!option.label?.trim()) error("11", `option ${i + 1} has no label: no button and nothing to match`, place)
      if (option.match?.includes("*")) error("12", `${name} matches "*": there is no catch-all`, place)
      if (option.label && option.match?.some(m => normalize(m) === normalize(option.label!))) warning("12.d", `${name} lists its own label as a match`, place)
      for (const value of new Set([option.id, option.label && normalize(option.label)].filter(Boolean) as string[])) {
        if (seen.has(value)) error("12.b", `${name} is the same as another option: one of them can never be picked`, place)
        seen.add(value)
      }
      option.conditions?.forEach(c => condition(c, place))
      outcome(option.action, place, name)
    })

    // The same, in each language: what is typed is matched against the translated label
    for (const lang of languages) {
      const labels = new Set<string>()
      list.forEach(option => {
        const translated = option.translations?.[lang]
        const text = translated?.label || option.label
        if (translated?.match?.includes("*")) error("12", `in ${lang}, ${text} matches "*": there is no catch-all`, place)
        if (!text) return
        if (labels.has(normalize(text))) error("12.b", `in ${lang}, ${text} is the same as another option: one of them can never be picked`, place)
        labels.add(normalize(text))
        if (translated?.label && translated.match?.some(m => normalize(m) === normalize(translated.label!))) warning("12.d", `in ${lang}, ${text} lists its own label as a match`, place)
      })
    }
  }

  function languageCode(code: string, what: string) {
    const canonical = canonicalLanguage(code)
    if (!canonical) error("lang", `${what}, ${code}, is not a language code`)
    else if (canonical !== code) error("lang", `${what} is written ${code}: write it ${canonical}`)
  }

  // Translations to a language that is not in use, of fields that do not translate, or of an empty default; templates
  // that do not compile or use other vars than the default; and, per language, what is still missing
  function translations(texts: Text[], place?: Place) {
    const missing = new Map<string, string[]>()
    for (const { what, target, fields } of texts) {
      if (!target) continue
      for (const [lang, values] of Object.entries(target.translations || {})) {
        if (lang === language) warning("lang", `${what} is translated to ${lang}, which is the default language: it is never used`, place)
        else if (!languages.includes(lang)) warning("lang", `${what} is translated to ${lang}, which is not a supported language`, place)
        for (const key of Object.keys(values || {})) {
          const text = value(values, key)
          if (!fields.includes(key)) { warning("lang", `${what} translates ${key} to ${lang}, but ${key} is not translatable`, place); continue }
          if (present(text) && !present(value(target, key))) warning("lang", `${what} has a ${lang} ${key} but no ${key} in the default language`, place)
          if (key === "say" && typeof text === "string") {
            template(text, `the ${lang} say of ${what}`, place)
            sameVars(value(target, key), text, `the ${lang} say of ${what}`, place)
          }
        }
      }
      for (const lang of languages) {
        const absent = fields.filter(f => present(value(target, f)) && !present(value(target.translations?.[lang], f)))
        if (absent.length) missing.set(lang, [...missing.get(lang) || [], `${what} (${absent.join(", ")})`])
      }
    }
    for (const [lang, list] of missing) warning("lang.missing", `not translated to ${lang}: ${list.join(", ")}. The default text goes out instead`, place)
  }

  function sameVars(original: unknown, text: string, what: string, place?: Place) {
    if (typeof original !== "string") return
    const a = scan(original), b = scan(text)
    if (a.error || b.error) return
    const lost = a.reads.filter(name => !b.reads.includes(name)), extra = b.reads.filter(name => !a.reads.includes(name))
    if (lost.length) warning("lang", `${what} does not use ${[...new Set(lost)].join(", ")}, which the default does`, place)
    if (extra.length) warning("lang", `${what} uses ${[...new Set(extra)].join(", ")}, which the default does not`, place)
  }

  function tools(item: PlaybookItem, place: Place) {
    item.references?.forEach((r, i) => {
      if (!r.description?.trim()) error("tools", `reference ${i + 1} has no description, so the model cannot know when to read it`, place)
      if (!r.text?.trim()) error("tools", `reference ${i + 1} is empty`, place)
    })

    const vars = new Set<string>()
    item.captures?.forEach((c, i) => {
      if (!c.var?.trim()) return error("tools", `capture ${i + 1} names no var`, place)
      if (vars.has(c.var)) error("tools", `${c.var} is captured twice`, place)
      vars.add(c.var)
      if (!c.description?.trim()) error("tools", `the capture of ${c.var} has no description, so the model cannot know what to save`, place)
      if (c.type === "enum" && !c.values?.length) error("tools", `the capture of ${c.var} is an enum with no values`, place)
    })

    const types = new Set<string>()
    item.tools?.forEach(t => {
      if ((t.type === "notes" || t.type === "calculator") && types.has(t.type)) error("tools", `${t.type} is declared twice`, place)
      types.add(t.type)
      if ("description" in t && !t.description?.trim()) error("tools", `the ${t.type} tool has no description`, place)
      if (t.type === "search" && !t.sources?.length) warning("tools", "the search tool has no sources: it always finds nothing", place)
      if (t.type === "generate" && !t.prompt?.trim()) error("tools", "the generator has no prompt", place)
    })
  }

  // 21.b: going back up to an item gated on isEmpty does nothing unless the way back forgets the var
  function reenter(writer: Writer, place: Place) {
    const action = writer.action
    if (action?.type !== "goto" || action.flow !== place.flow) return
    const destination = find(action)
    if (!destination || destination.index > place.index) return
    const forgets = Object.entries(writer.set || {}).filter(([, value]) => value == null).map(([name]) => name)
    for (const [name, test] of destination.item.conditions || []) {
      if (test === "isEmpty" && !forgets.includes(name)) {
        error("21.b", `goes back to ${destination.item.id}, which is skipped while ${name} is set, without setting ${name} to null`, place)
      }
    }
  }

  // 15.b: every template parses
  function template(text: string | undefined, what: string, place?: Place) {
    if (!text?.includes("{{")) return
    const found = scan(text)
    if (found.error) return error("15.b", `${what} does not compile: ${found.error}`, place)
    found.reads.forEach(name => read(name, place))
  }
}

function issue(level: PlaybookIssue["level"], rule: string, message: string, place?: Place): PlaybookIssue {
  return place ? { level, rule, message, flow: place.flow, index: place.index, item: place.item.id } : { level, rule, message }
}

function label(cursor: PlaybookCursor) {
  return `${cursor.flow}.${cursor.item}`
}

function canonicalLanguage(code: string) {
  try { return Intl.getCanonicalLocales(code)[0] } catch { return undefined }
}

function value(target: object | undefined, key: string) {
  return (target as Record<string, unknown> | undefined)?.[key]
}

function present(value: unknown) {
  return Array.isArray(value) ? value.length > 0 : typeof value === "string" ? !!value.trim() : value != null
}

function firstLine(e: any) {
  return String(e?.message || e).split("\n")[0]
}

// The vars a template reads. Reads under an #if or #unless are not counted: testing a var that may be
// missing is what those blocks are for
function scan(text: string): { error?: string, reads: string[] } {
  const reads: string[] = []
  let ast: any
  try { ast = Handlebars.parse(text) } catch (e) { return { error: firstLine(e), reads } }

  const path = (node: any, guarded: boolean) => {
    if (!guarded && node.type === "PathExpression" && !node.data && node.parts.length) reads.push(node.parts[0])
  }

  const call = (node: any, guarded: boolean) => {
    const head = node.path.original
    if (!node.params.length && !HELPERS.includes(head) && node.type !== "SubExpression") path(node.path, guarded)
    for (const param of [...node.params, ...(node.hash?.pairs || []).map((pair: any) => pair.value)]) {
      if (param.type === "SubExpression") call(param, guarded)
      else path(param, guarded)
    }
  }

  const statements = (body: any[], guarded: boolean) => body.forEach(node => {
    if (node.type === "MustacheStatement") call(node, guarded)
    if (node.type !== "BlockStatement") return
    const guard = guarded || node.path.original === "if" || node.path.original === "unless"
    call(node, guard)
    statements(node.program?.body || [], guard)
    statements(node.inverse?.body || [], guard)
  })

  statements(ast.body, false)
  return { reads }
}
